import { useNavigate, useLocation } from "react-router-dom";
import { Package, BarChart3, Archive, Settings, ShoppingBag, HelpCircle, Users, LogOut } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useUserFeatures } from "@/hooks/useUserFeatures";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { getPreloadKey, preloadRoute } from "@/lib/routePreload";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import { MobileNavContent } from "@/components/MobileNav";
import { LogoutConfirmDialog } from "@/components/LogoutConfirmDialog";
import { requestFCMToken } from "@/firebase";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useCallback, useState } from "react";


interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * AppSidebar - Navigation sidebar without internal header
 * 
 * The toggle + logo now live in AppNavigation's header-left column.
 * This sidebar starts directly with nav items.
 * 
 * On mobile: renders MobileNavContent inside the Sheet (hamburger drawer)
 * On desktop: renders the persistent sidebar with icons
 */
export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { data: userFeatures } = useUserFeatures();
  const { setShowOnboarding } = useOnboarding();
  const isMobile = useIsMobile();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setShowLogoutDialog(false);
  };

  // Check feature flags
  const externalIntakeEnabled = userFeatures?.external_order_intake ?? false;
  const customersModuleEnabled = userFeatures?.customers_module ?? false;

  // All nav items in a single flat list (no section headings per Optimo spec)
  const navItems: NavItem[] = [
    {
      title: t("nav.orders"),
      url: "/orders",
      icon: Package,
    },
    // Only show Products if external intake is enabled
    ...(externalIntakeEnabled ? [{
      title: t("nav.products"),
      url: "/products",
      icon: ShoppingBag,
    }] : []),
    // Only show Customers if module is enabled (after Products)
    ...(customersModuleEnabled ? [{
      title: t("nav.customers"),
      url: "/customers",
      icon: Users,
    }] : []),
    {
      title: t("nav.dashboard"),
      url: "/dashboard",
      icon: BarChart3,
    },
    {
      title: t("nav.archive"),
      url: "/archive",
      icon: Archive,
    },
    {
      title: t("nav.configuration"),
      url: "/settings",
      icon: Settings,
    },
  ];
  
  // Handle Help click - opens onboarding modal via context
  const handleHelpClick = useCallback(() => {
    setShowOnboarding(true);
  }, [setShowOnboarding]);
  
  
  useEffect(()=>{
    //enable notifications for push notifications
  async function enableNotifications() {
      //get the user id from supabase
    const { data: { session } } = await supabase.auth.getSession();
    const token = await requestFCMToken();
    if (!token) return;
  
    await supabase
    .from("device_tokens")
    .upsert({ user_id: session?.user?.id, token });
  }

  enableNotifications()

  },[])

  const isActive = (url: string) => location.pathname === url;

  // Preload route on hover for instant navigation
  const handleMouseEnter = useCallback((url: string) => {
    const key = getPreloadKey(url);
    if (key) {
      preloadRoute(key);
    }
  }, []);

  return (
    <Sidebar collapsible="icon">
      {/* Mobile: render MobileNavContent for the hamburger drawer */}
      {isMobile ? (
        <MobileNavContent />
      ) : (
        <>
          {/* No SidebarHeader - toggle + logo are in the header now */}
          <SidebarContent className="bg-sidebar pt-4">
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                {navItems.map((item) => {
                    const active = isActive(item.url);
                    return (
                      <SidebarMenuItem key={item.url}>
                        <SidebarMenuButton
                          onClick={() => navigate(item.url)}
                          onMouseEnter={() => handleMouseEnter(item.url)}
                          isActive={active}
                          size="lg"
                          className={cn(
                            "transition-colors",
                            !active && "text-sidebar-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent/30",
                            active && "!bg-transparent text-sidebar-primary font-medium hover:!bg-transparent"
                          )}
                          aria-current={active ? "page" : undefined}
                        >
                          <item.icon className={cn(
                            "h-[18px] w-[18px] transition-colors shrink-0",
                            active ? "text-sidebar-primary" : "text-sidebar-foreground"
                          )} />
                          <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}

                  {/* Help / Guide */}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={handleHelpClick}
                      size="lg"
                      className="text-sidebar-foreground transition-colors hover:text-sidebar-accent-foreground hover:bg-sidebar-accent/50"
                    >
                      <HelpCircle className="h-[18px] w-[18px] transition-colors shrink-0 text-sidebar-foreground" />
                      <span className="group-data-[collapsible=icon]:hidden">{t("nav.showGuide")}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="pb-4 bg-sidebar border-t border-sidebar-border/40">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => setShowLogoutDialog(true)}
                  size="lg"
                  className="text-sidebar-foreground transition-colors hover:text-sidebar-accent-foreground hover:bg-sidebar-accent/50"
                >
                  <LogOut className="h-[18px] w-[18px] shrink-0 text-sidebar-foreground" />
                  <span className="group-data-[collapsible=icon]:hidden">{t("common.signOut")}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </>
      )}

      <LogoutConfirmDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={handleSignOut}
      />
    </Sidebar>
  );
}
